/**
 * Mira on the Overview: start a competitor chat and see what competitor
 * discovery found.
 */
import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { chat as chatApi, competitors as competitorsApi, kyc } from "@/lib/api";
import type { Competitor } from "@/types";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { timeAgo } from "@/lib/utils";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

type Discovery = Awaited<ReturnType<typeof kyc.discovery>> extends { data?: infer D } ? D : never;
type ChatSession = { id: string; domain: string; title: string; updated_at: string };

export function MiraPanel() {
  const [isLoading, setIsLoading] = useState(true);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [discovery, setDiscovery] = useState<Discovery | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const currentRequestId = ++requestId.current;
    setIsLoading(true);

    const [competitorsResult, sessionsResult, discoveryResult] = await Promise.allSettled([
      competitorsApi.list(),
      chatApi.sessions(),
      kyc.discovery(),
    ]);

    // Ignore responses from a previous workspace if a later refresh has started.
    if (currentRequestId !== requestId.current) return;

    setCompetitors(
      competitorsResult.status === "fulfilled" && "data" in competitorsResult.value && competitorsResult.value.data
        ? competitorsResult.value.data
        : [],
    );
    setSessions(
      sessionsResult.status === "fulfilled" && "data" in sessionsResult.value && sessionsResult.value.data
        ? sessionsResult.value.data
        : [],
    );
    setDiscovery(
      discoveryResult.status === "fulfilled" && "data" in discoveryResult.value
        ? discoveryResult.value.data ?? null
        : null,
    );
    setIsLoading(false);
  }, []);

  useWorkspaceRefresh(load);

  return (
    <section>
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="magic-wand-01" size={12} className="text-muted-foreground" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Mira</span>
        </div>
        <span className="font-sans text-[11px] text-muted-foreground">Your competitive intelligence engine</span>
      </div>

      <div className="grid grid-cols-1 border border-border rounded-lg lg:grid-cols-2">
        <Cell
          title="Ask Mira"
          icon="bubble-chat"
          href="/chat"
          linkLabel="Open chat"
          className="border-b border-border lg:border-b-0 lg:border-r rounded-t-lg lg:rounded-tr-none lg:rounded-l-lg"
        >
          {isLoading ? (
            <LoadingLines />
          ) : competitors.length === 0 ? (
            <p className="font-sans text-xs text-muted-foreground">
              Add a competitor to your watchlist, then ask Mira about their pricing, positioning or latest moves.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {competitors.slice(0, 4).map((c) => (
                  <Link
                    key={c.id}
                    to={`/chat?competitor=${encodeURIComponent(c.domain)}`}
                    className="rounded-md border border-border px-2 py-1 font-mono text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
              {sessions.length > 0 && (
                <ul className="space-y-1">
                  {sessions.slice(0, 2).map((s) => (
                    <li key={s.id}>
                      <Link to={`/chat/${s.id}`} className="flex items-baseline justify-between gap-3 font-sans text-xs hover:underline underline-offset-2">
                        <span className="truncate">{s.title}</span>
                        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{timeAgo(s.updated_at)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Cell>

        <Cell
          title="Know your competitors"
          icon="radar-01"
          href="/know-your-competitors"
          linkLabel={discovery ? "See the landscape" : "Run discovery"}
          className="rounded-b-lg lg:rounded-bl-none lg:rounded-r-lg"
        >
          {isLoading ? (
            <LoadingLines />
          ) : discovery ? (
            <div className="space-y-2">
              <p className="font-mono text-3xl font-bold leading-none tabular-nums">{discovery.competitors.length}</p>
              <p className="font-sans text-xs text-muted-foreground">
                competitors found in {discovery.productCategory}
                {" · "}updated {timeAgo(discovery.refreshedAt ?? discovery.discoveredAt)}
              </p>
            </div>
          ) : (
            <p className="font-sans text-xs text-muted-foreground">
              Mira reads your site, works out your category and finds the companies you compete with, with their rank, traffic and page speed.
            </p>
          )}
        </Cell>
      </div>
    </section>
  );
}

function Cell({
  title,
  icon,
  href,
  linkLabel,
  className,
  children,
}: {
  title: string;
  icon: string;
  href: string;
  linkLabel: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-3 bg-card p-5 ${className ?? ""}`} style={{ minHeight: "150px" }}>
      <div className="flex items-start justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
        <div className="rounded border border-border p-1.5">
          <Icon name={icon} size={14} className="text-muted-foreground" />
        </div>
      </div>
      <div className="flex-1">{children}</div>
      <Link
        to={href}
        className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
      >
        {linkLabel}
        <Icon name="arrow-right-01" size={11} />
      </Link>
    </div>
  );
}

function LoadingLines() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-3 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}
